import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MediaLibraryDialog } from '../../../../../../../SUVO-MEDIA-CLIENT-LIB/modules/media/dialog/library/component';

type TRichTextEditor = {
  chain: () => any;
  commands: {
    setContent: (value: string) => void;
  };
  destroy: () => void;
  getAttributes: (mark: string) => Record<string, unknown>;
  getHTML: () => string;
  isActive: (mark: string, attributes?: Record<string, unknown>) => boolean;
  on: (eventName: string, handler: () => void) => void;
  setEditable: (isEditable: boolean) => void;
};

type TiptapModuleBundle = {
  Editor: new (options: {
    editable: boolean;
    element: HTMLDivElement;
    extensions: unknown[];
    content: string;
    onUpdate: ({ editor }: { editor: TRichTextEditor }) => void;
    onSelectionUpdate: () => void;
  }) => TRichTextEditor;
  Bold: unknown;
  BulletList: unknown;
  Document: unknown;
  Heading: {
    configure: (options: { levels: number[] }) => unknown;
  };
  HorizontalRule: unknown;
  Image: unknown;
  Italic: unknown;
  Link: {
    configure: (options: { openOnClick: boolean; autolink: boolean }) => unknown;
  };
  ListItem: unknown;
  OrderedList: unknown;
  Paragraph: unknown;
  Strike: unknown;
  TableKit: unknown;
  TableCell: unknown;
  TableHeader: unknown;
  TableRow: unknown;
  Text: unknown;
  Underline: unknown;
};

@Component({
  selector: 'suvo-property-rich-text-control',
  standalone: true,
  imports: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RichTextControlComponent,
      multi: true,
    },
  ],
  styles: `
    :host {
      width: 100%;
      height: 100%;
      min-height: 460px;
      display: flex;
      flex-direction: column;
      border: 1px solid #ccc;
      border-radius: 4px;
      padding: 8px;
      box-sizing: border-box;
      gap: 8px;
      background: #fff;
    }

    .toolbar {
      display: flex;
      width: 100%;
      flex: 0 0 auto;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 6px;
    }

    .toolbar button {
      border: 1px solid #c6c6c6;
      background: #f7f7f7;
      border-radius: 4px;
      min-width: 36px;
      min-height: 36px;
      padding: 0 10px;
      cursor: pointer;
    }

    .toolbar button.active {
      background: #dfe7ff;
      border-color: #7d93d9;
    }

    .toolbar button:disabled {
      cursor: default;
      opacity: 0.5;
    }

    .toolbar .mode {
      display: inline-flex;
      align-items: center;
      padding: 0 8px;
      border-radius: 999px;
      background: #eef2ff;
      color: #334155;
      font-size: 12px;
      line-height: 28px;
      height: 28px;
      margin-left: auto;
    }

    .toolbar .mode.fallback {
      background: #f3f4f6;
      color: #6b7280;
    }

    .editor-area {
      flex: 1 1 auto;
      min-height: 380px;
      width: 100%;
      border: 1px solid #c4c4c4;
      border-radius: 4px;
      padding: 12px;
      box-sizing: border-box;
      overflow: auto;
      overflow-wrap: anywhere;
      outline: none;
      background: white;
    }

    .editor-area:empty::before {
      content: attr(data-placeholder);
      color: #888;
      font-style: italic;
    }

    .editor-area img {
      max-width: 100%;
      height: auto;
    }

    .editor-area table {
      width: auto;
      border-collapse: collapse;
    }

    .editor-area table,
    .editor-area th,
    .editor-area td {
      border: 1px solid #000;
    }

    .editor-area th,
    .editor-area td {
      padding: 4px 6px;
    }
  `,
  template: `
    <div class="toolbar">
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('bold')" (click)="exec('bold')">B</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('italic')" (click)="exec('italic')">I</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('underline')" (click)="exec('underline')">U</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('strikeThrough')" (click)="exec('strikeThrough')">S</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('formatBlock', 'h1')" (click)="setHeading('h1')">H1</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('formatBlock', 'h2')" (click)="setHeading('h2')">H2</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('formatBlock', 'h3')" (click)="setHeading('h3')">H3</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('formatBlock', 'h4')" (click)="setHeading('h4')">H4</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('insertUnorderedList')" (click)="exec('insertUnorderedList')">• List</button>
      <button type="button" [disabled]="isDisabled" [class.active]="isCommandActive('insertOrderedList')" (click)="exec('insertOrderedList')">1. List</button>
      <button type="button" [disabled]="isDisabled" (click)="exec('insertHorizontalRule')">HR</button>
      <button type="button" [disabled]="isDisabled" (click)="setLink()">Link</button>
      <button type="button" [disabled]="isDisabled" (click)="addImage()">Image</button>
      <button type="button" [disabled]="isDisabled" (click)="addTable()">Table</button>
      <span class="mode" [class.fallback]="!enhancedModeEnabled">
        {{ enhancedModeEnabled ? 'Enhanced mode' : 'Basic mode' }}
      </span>
    </div>
    <div
      #editorElement
      class="editor-area"
      role="textbox"
      aria-multiline="true"
      [attr.aria-label]="'Rich text editor'"
      data-placeholder="Write rich text here"
      [attr.contenteditable]="enhancedModeEnabled ? null : !isDisabled"
      (input)="handleInput()"
      (blur)="onTouched()"
    ></div>
  `,
})
export class RichTextControlComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly cd = inject(ChangeDetectorRef);

  private editor: TRichTextEditor | null = null;
  value = '';
  isDisabled = false;
  enhancedModeEnabled = false;
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};

  @ViewChild('editorElement') editorElementRef?: ElementRef<HTMLDivElement>;

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  writeValue(value: string): void {
    this.value = value || '';

    if (this.editor) {
      this.editor.commands.setContent(this.value);
      return;
    }

    if (this.editorElementRef) {
      this.editorElementRef.nativeElement.innerHTML = this.value;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
    this.cd.detectChanges();
  }

  async ngAfterViewInit(): Promise<void> {
    await this.initializeEnhancedMode();

    if (this.editorElementRef) {
      this.editorElementRef.nativeElement.innerHTML = this.value;
    }

    this.cd.detectChanges();
  }

  handleInput(): void {
    if (this.editor || !this.editorElementRef) {
      return;
    }

    const html = this.editorElementRef.nativeElement.innerHTML;
    this.value = html;
    this.onChange(html);
    this.cd.detectChanges();
  }

  exec(command: string, value?: string): void {
    if (this.isDisabled) {
      return;
    }

    if (this.editor) {
      this.runEnhancedCommand(command, value);
      return;
    }

    if (!this.editorElementRef) {
      return;
    }

    this.focusEditor();
    document.execCommand(command, false, value);
    this.handleInput();
  }

  setHeading(level: 'h1' | 'h2' | 'h3' | 'h4'): void {
    if (this.editor) {
      this.editor.chain().focus().toggleHeading({ level: Number(level.slice(1)) }).run();
      return;
    }

    this.exec('formatBlock', level);
  }

  isCommandActive(command: string, value?: string): boolean {
    if (this.editor) {
      switch (command) {
        case 'bold':
          return this.editor.isActive('bold');
        case 'italic':
          return this.editor.isActive('italic');
        case 'underline':
          return this.editor.isActive('underline');
        case 'strikeThrough':
          return this.editor.isActive('strike');
        case 'insertUnorderedList':
          return this.editor.isActive('bulletList');
        case 'insertOrderedList':
          return this.editor.isActive('orderedList');
        case 'formatBlock':
          return value ? this.editor.isActive('heading', { level: Number(value.slice(1)) }) : false;
        default:
          return false;
      }
    }

    try {
      if (value) {
        return document.queryCommandValue(command)?.toLowerCase?.() === value.toLowerCase();
      }

      return document.queryCommandState(command);
    } catch {
      return false;
    }
  }

  setLink(): void {
    if (this.isDisabled) {
      return;
    }

    if (this.editor) {
      if (this.editor.isActive('link')) {
        this.editor.chain().focus().unsetLink().run();
        return;
      }

      const previousUrl = this.editor.getAttributes('link').href;
      const url = window.prompt('URL', typeof previousUrl === 'string' ? previousUrl : '');

      if (url === null) {
        return;
      }

      if (url.trim() === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
        return;
      }

      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim() }).run();
      return;
    }

    if (!this.editorElementRef) {
      return;
    }

    const url = window.prompt('URL');
    if (url === null) {
      return;
    }

    if (url.trim() === '') {
      this.exec('unlink');
      return;
    }

    this.focusEditor();
    document.execCommand('createLink', false, url.trim());
    this.handleInput();
  }

  addTable(): void {
    if (this.isDisabled) {
      return;
    }

    if (this.editor) {
      this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      return;
    }

    if (!this.editorElementRef) {
      return;
    }

    const tableHtml = `
      <table>
        <tbody>
          <tr><th>Heading 1</th><th>Heading 2</th><th>Heading 3</th></tr>
          <tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
          <tr><td>&nbsp;</td><td>&nbsp;</td><td>&nbsp;</td></tr>
        </tbody>
      </table>
    `;

    this.focusEditor();
    document.execCommand('insertHTML', false, tableHtml);
    this.handleInput();
  }

  addImage(): void {
    if (this.isDisabled) {
      return;
    }

    const dialogRef = this.dialog.open(MediaLibraryDialog, {
      width: '1000px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe((imageId: string) => {
      if (!imageId) {
        return;
      }

      const imageUrl = `https://suvo-services-dev-media.s3.eu-west-1.amazonaws.com/suvo_bi/613bc6cf38a5491035954114/${imageId}/thumbnail`;

      if (this.editor) {
        this.editor.chain().focus().setImage({
          src: imageUrl,
          alt: 'Descriptive alt text',
          title: 'Image title',
        }).run();
        return;
      }

      if (!this.editorElementRef) {
        return;
      }

      const imageHtml = `<img src="${imageUrl}" alt="Descriptive alt text" title="Image title" />`;

      this.focusEditor();
      document.execCommand('insertHTML', false, imageHtml);
      this.handleInput();
    });
  }

  private focusEditor(): void {
    this.editorElementRef?.nativeElement.focus();
  }

  private async initializeEnhancedMode(): Promise<void> {
    try {
      const modules = (await this.loadOptionalTiptapModules()) as TiptapModuleBundle;

      this.editor = new modules.Editor({
        editable: !this.isDisabled,
        element: this.editorElementRef!.nativeElement,
        extensions: [
          modules.Document,
          modules.Paragraph,
          modules.Text,
          modules.Bold,
          modules.Italic,
          modules.Underline,
          modules.Strike,
          modules.BulletList,
          modules.ListItem,
          modules.OrderedList,
          modules.HorizontalRule,
          modules.Link.configure({
            openOnClick: false,
            autolink: true,
          }),
          modules.Heading.configure({
            levels: [1, 2, 3, 4],
          }),
          modules.Image,
          modules.TableKit,
          modules.TableRow,
          modules.TableHeader,
          modules.TableCell,
        ],
        content: this.value,
        onUpdate: ({ editor }) => {
          const html = editor.getHTML();
          this.value = html;
          this.onChange(html);
          this.cd.detectChanges();
        },
        onSelectionUpdate: () => {
          this.cd.detectChanges();
        },
      });

      this.editor.on('blur', () => {
        this.onTouched();
      });

      this.enhancedModeEnabled = true;
    } catch {
      this.editor?.destroy();
      this.editor = null;
      this.enhancedModeEnabled = false;
    }
  }

  private async loadOptionalTiptapModules(): Promise<unknown> {
    const optionalImport = async <T>(specifier: string): Promise<T> => import(specifier) as Promise<T>;

    const [coreModule, boldModule, bulletListModule, documentModule, headingModule, horizontalRuleModule, imageModule, italicModule, linkModule, listItemModule, orderedListModule, paragraphModule, strikeModule, tableModule, tableCellModule, tableHeaderModule, tableRowModule, textModule, underlineModule] = await Promise.all([
      optionalImport<{ Editor: TiptapModuleBundle['Editor'] }>('@tiptap/core'),
      optionalImport<{ default: unknown }>('@tiptap/extension-bold'),
      optionalImport<{ default: unknown }>('@tiptap/extension-bullet-list'),
      optionalImport<{ default: unknown }>('@tiptap/extension-document'),
      optionalImport<TiptapModuleBundle['Heading']>('@tiptap/extension-heading'),
      optionalImport<{ default: unknown }>('@tiptap/extension-horizontal-rule'),
      optionalImport<{ default: unknown }>('@tiptap/extension-image'),
      optionalImport<{ default: unknown }>('@tiptap/extension-italic'),
      optionalImport<TiptapModuleBundle['Link']>('@tiptap/extension-link'),
      optionalImport<{ default: unknown }>('@tiptap/extension-list-item'),
      optionalImport<{ default: unknown }>('@tiptap/extension-ordered-list'),
      optionalImport<{ default: unknown }>('@tiptap/extension-paragraph'),
      optionalImport<{ default: unknown }>('@tiptap/extension-strike'),
      optionalImport<{ TableKit: unknown }>('@tiptap/extension-table'),
      optionalImport<{ default: unknown }>('@tiptap/extension-table-cell'),
      optionalImport<{ default: unknown }>('@tiptap/extension-table-header'),
      optionalImport<{ default: unknown }>('@tiptap/extension-table-row'),
      optionalImport<{ default: unknown }>('@tiptap/extension-text'),
      optionalImport<{ default: unknown }>('@tiptap/extension-underline'),
    ]);

    return {
      Editor: coreModule.Editor,
      Bold: boldModule.default,
      BulletList: bulletListModule.default,
      Document: documentModule.default,
      Heading: headingModule,
      HorizontalRule: horizontalRuleModule.default,
      Image: imageModule.default,
      Italic: italicModule.default,
      Link: linkModule,
      ListItem: listItemModule.default,
      OrderedList: orderedListModule.default,
      Paragraph: paragraphModule.default,
      Strike: strikeModule.default,
      TableKit: tableModule.TableKit,
      TableCell: tableCellModule.default,
      TableHeader: tableHeaderModule.default,
      TableRow: tableRowModule.default,
      Text: textModule.default,
      Underline: underlineModule.default,
    };
  }

  private runEnhancedCommand(command: string, value?: string): void {
    if (!this.editor) {
      return;
    }

    const chain = this.editor.chain().focus();

    switch (command) {
      case 'bold':
        chain.toggleBold().run();
        return;
      case 'italic':
        chain.toggleItalic().run();
        return;
      case 'underline':
        chain.toggleUnderline().run();
        return;
      case 'strikeThrough':
        chain.toggleStrike().run();
        return;
      case 'insertUnorderedList':
        chain.toggleBulletList().run();
        return;
      case 'insertOrderedList':
        chain.toggleOrderedList().run();
        return;
      case 'insertHorizontalRule':
        chain.setHorizontalRule().run();
        return;
      case 'formatBlock':
        if (value) {
          chain.toggleHeading({ level: Number(value.slice(1)) }).run();
        }
        return;
      default:
        return;
    }
  }
}
